'use client';

/**
 * LOCI authentication context (RTK Query-backed).
 *
 * **IMPORTANT — Callers MUST route login/register through this context.**
 *
 * Do NOT call `useLoginMutation()` / `useRegisterMutation()` from `@loci/api`
 * directly in UI components. The `login` / `register` functions exposed here
 * own the full set of side effects every auth transition must execute:
 *
 *   1. Persist the access token in memory for Bearer headers (`setAccessToken`).
 *   2. Write the localStorage session hint (`setSessionHint`) so subsequent
 *      navigations can bootstrap without an extra 401 probe.
 *   3. Enable the `/auth/me` bootstrap query (`setShouldFetchUser(true)`).
 *   4. Navigate to the post-auth destination (default `/`, overridable
 *      via the optional `redirect` option). The proxy redirects `/` to
 *      the user's resolved city (Plan 5); the legacy `/dashboard` literal
 *      that lived here pre-Plan-5 is now intercepted by the 308 redirect
 *      to `/<DEFAULT_CITY>`.
 *
 * Bypassing this surface leaves the client in an inconsistent state whenever
 * the post-auth redirect is a public route (the stale-cookie recovery only
 * re-hydrates on protected routes). It also silently skips future side
 * effects (analytics, token-refresh scheduling, etc.) that may be added here.
 *
 * See `docs/auth-issues-2026-04-20.md` issue #7 for the original incident.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { useRouter } from 'next/navigation';
import {
  useGetCurrentUserQuery,
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useRefreshTokenMutation,
} from '@loci/api';
import type { User, LoginRequest, RegisterRequest } from '@loci/api';
import {
  AuthApiError,
  transformRTKError,
  extractErrorMessage,
  isAuthenticationError,
} from '../lib/auth/rtk-error-adapter';
import { hasSessionHint, setSessionHint } from '../lib/auth/sessionHint';
import { clearClientSessionResidue, sanitizeInvalidSession } from '../lib/auth/sessionSanitize';
import {
  crossHostLogin,
  getValidCrossHostRedirect,
  navigateToValidTarget,
} from '@/lib/cross-host-redirect';

export interface AuthContextValue {
  // State
  user: User | null;
  /** In-memory access token when available (e.g. after login/refresh). Used for Authorization header. */
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loading: boolean; // Alias for isLoading for backward compatibility
  error: string | null;

  // Actions
  /**
   * Sign in with email/password. Executes every required side effect
   * (token capture, session hint, `/auth/me` bootstrap) and then navigates
   * to `options.redirect`. Invalid or missing redirect falls back to `/`
   * (Plan 4 — was `/dashboard` pre-cutover).
   */
  login: (credentials: LoginRequest, options?: { redirect?: string }) => Promise<void>;
  /**
   * Register a new account. Mirrors `login` on success.
   *
   * `options.skipRedirect` suppresses **only** the final navigation step —
   * every other side effect (token capture, session hint, `/auth/me`
   * bootstrap) still fires. The signup page uses this to interpose a
   * non-blocking post-signup passkey prompt (FRE-841) and own the navigation
   * itself once the prompt is dismissed or actioned. Defaults to `false`, so
   * existing callers keep the navigate-on-success behavior unchanged.
   */
  register: (
    userData: RegisterRequest,
    options?: { redirect?: string; skipRedirect?: boolean },
  ) => Promise<void>;
  /**
   * Sign out: tear down client session residue, call the logout endpoint, and
   * hard-navigate. `redirectTo` (FRE-1389) is where a SUCCESSFUL sign-out
   * lands — e.g. the city page the visitor signed out from. It is validated to
   * a LOCI host / same-origin path; a failed logout (the server session may
   * still be live) or an invalid target always goes to the app-host login.
   */
  logout: (options?: { redirectTo?: string }) => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User | null) => void;
  /**
   * Force the `/auth/me` bootstrap query to run and refresh the session hint.
   * Call this from protected routes where the server layout has already
   * verified httpOnly auth cookies exist — bypasses the localStorage hint gate
   * so a browser with valid cookies but a cleared/stale hint isn't trapped in
   * an "unauthenticated" client state.
   */
  bootstrapSession: () => void;
}

/** Exported for Storybook/mock providers when the full AuthProvider cannot run (e.g. static Chromatic). */
export const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  /** In-memory access token from login/register/refresh; cleared on logout. Used for Bearer header when making fetch requests. */
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // Local user state override for setUser compatibility
  const [localUserOverride, setLocalUserOverride] = useState<User | null | undefined>(undefined);

  // Read the session hint after mount to avoid SSR hydration mismatch.
  // When no hint is present we skip the bootstrap query entirely so anonymous
  // visitors don't generate a guaranteed 401 on every public page.
  const [shouldFetchUser, setShouldFetchUser] = useState(false);
  useEffect(() => {
    setShouldFetchUser(hasSessionHint());
  }, []);

  // RTK Query hooks
  const {
    data: userData,
    isLoading: isQueryLoading,
    isError: isQueryError,
    isSuccess: isQuerySuccess,
    error: queryError,
    status: queryStatus,
    refetch: refetchUser,
  } = useGetCurrentUserQuery(undefined, {
    // Treat 401 as "not authenticated" state, not an error
    // This prevents error state during initialization when user is not logged in
    // WEB-810: keep false so CityChrome / AppInitializer remounts reuse cache.
    refetchOnMountOrArgChange: false,
    skip: !shouldFetchUser,
  });

  const [loginMutation, { isLoading: isLoginLoading }] = useLoginMutation();
  const [registerMutation, { isLoading: isRegisterLoading }] = useRegisterMutation();
  const [logoutMutation, { isLoading: isLogoutLoading }] = useLogoutMutation();
  const [refreshMutation, { isLoading: isRefreshLoading }] = useRefreshTokenMutation();

  // Derive state from RTK Query cache with local override support
  // If localUserOverride is set (not undefined), use it; otherwise use RTK Query data
  const userFromQuery = userData?.user ?? null;
  const user = localUserOverride !== undefined ? localUserOverride : userFromQuery;
  const isAuthenticated = user !== null;
  const isLoading =
    isQueryLoading || isLoginLoading || isRegisterLoading || isLogoutLoading || isRefreshLoading;

  // Detect and recover from stale auth cookies.
  //
  // Scenario: the browser has `accessToken`/`refreshToken` cookies that are
  // present but invalid — an expired session, a different JWT secret, a
  // different DB, or a server-side revocation. proxy.ts and the protected
  // server layout both treat cookie *presence* as "authed", so they route the
  // user into the app surface. The `/auth/me` bootstrap then resolves as
  // unauthenticated, but the cookies are still on the client.
  //
  // When bootstrap resolves unauthenticated we funnel into the shared
  // `sanitizeInvalidSession()` routine: it clears the session hint, best-effort
  // evicts the cookies (refreshing the CSRF token first so an expired `_csrf`
  // can't block logout), and — on the app surface — hard-redirects to
  // `/login?force=1`. The `?force=1` escape hatch is the load-bearing change vs.
  // the prior `window.location.replace('/')`: it makes proxy.ts serve the login
  // form regardless of the still-present stale cookie, instead of bouncing
  // `/` → `/<city>` (the feed) and trapping the user.
  const staleClearedRef = useRef(false);
  useEffect(() => {
    if (staleClearedRef.current) return;
    if (!shouldFetchUser) return;
    if (isQueryLoading) return;
    // Query resolved. Stale cookies surface as either:
    //  - isQueryError with HTTP 401/403 (auth failure — stale/invalid cookies)
    //  - isQuerySuccess with userData.user == null (the /auth/me 401 is
    //    transformed to a fulfilled { user: null } by the endpoint).
    // Network errors, 500s, etc. are NOT treated as auth failures — the user may
    // still have valid cookies and we shouldn't force-logout on transient issues.
    const isAuthFailure =
      isQueryError &&
      queryError != null &&
      'status' in queryError &&
      (queryError.status === 401 || queryError.status === 403);
    const resolvedUnauthed =
      isAuthFailure || (isQuerySuccess && (userData === undefined || userData.user == null));
    if (!resolvedUnauthed) return;
    staleClearedRef.current = true;
    setShouldFetchUser(false);
    void sanitizeInvalidSession();
  }, [shouldFetchUser, isQueryLoading, isQueryError, isQuerySuccess, userData, queryError]);

  /**
   * Login user with email and password.
   *
   * Side effects executed on success (in order):
   *   1. Capture access token in memory for Bearer headers.
   *   2. Write the localStorage session hint.
   *   3. Enable the `/auth/me` bootstrap query.
   *   4. Navigate to `options.redirect`. Cross-host targets use
   *      `window.location.assign`; same-host paths use `router.push`.
   *      Invalid or missing redirect falls back to `/`.
   *
   * When `options.redirect` points at a **public** route, step 3 is what
   * guarantees the client auth state flips within the same tick — without
   * it the UI would remain "unauthenticated" until the user happened to
   * visit a protected page.
   */
  const login = useCallback(
    async (credentials: LoginRequest, options?: { redirect?: string }) => {
      try {
        setError(null);

        const response = await loginMutation(credentials).unwrap();

        if (response.user) {
          if (response.accessToken) setAccessToken(response.accessToken);
          setSessionHint();
          setShouldFetchUser(true);
          // Plan 4 — cross-host post-auth redirect.
          // `getValidCrossHostRedirect` accepts both same-origin paths and
          // absolute URLs on the LOCI eTLD+1 (e.g. `https://clt.loci.city/...`).
          // `navigateToValidTarget` owns the cross-host hard-navigation vs
          // same-host App Router split (shared with `register` + the signup page).
          const target = getValidCrossHostRedirect(options?.redirect ?? null, '/');
          navigateToValidTarget(router, target);
        } else {
          throw new Error('Login failed');
        }
      } catch (err) {
        let errorMessage = 'Login failed';

        // Transform RTK error to AuthApiError for backward compatibility
        const transformedError = transformRTKError(err as Parameters<typeof transformRTKError>[0]);

        if (transformedError instanceof AuthApiError) {
          errorMessage = transformedError.apiError.message;
        } else if (transformedError instanceof Error) {
          errorMessage = transformedError.message;
        }

        setError(errorMessage);
        throw transformedError; // Re-throw so the UI can handle it
      }
    },
    [loginMutation, router],
  );

  /**
   * Register new user. See `login` for the side-effect contract — `register`
   * behaves identically on success.
   */
  const register = useCallback(
    async (userData: RegisterRequest, options?: { redirect?: string; skipRedirect?: boolean }) => {
      try {
        setError(null);

        const response = await registerMutation(userData).unwrap();

        if (response.user) {
          if (response.accessToken) setAccessToken(response.accessToken);
          setSessionHint();
          setShouldFetchUser(true);
          // Plan 4 — cross-host post-auth redirect (same contract as `login`).
          // FRE-841: `skipRedirect` lets the signup page hold navigation while a
          // post-signup passkey prompt is shown; the side effects above still
          // run so the session is fully hydrated by the time the page navigates.
          if (!options?.skipRedirect) {
            const target = getValidCrossHostRedirect(options?.redirect ?? null, '/');
            navigateToValidTarget(router, target);
          }
        } else {
          throw new Error('Registration failed');
        }
      } catch (err) {
        let errorMessage = 'Registration failed';

        // Transform RTK error to AuthApiError for backward compatibility
        const transformedError = transformRTKError(err as Parameters<typeof transformRTKError>[0]);

        if (transformedError instanceof AuthApiError) {
          errorMessage = transformedError.apiError.message;
        } else if (transformedError instanceof Error) {
          errorMessage = transformedError.message;
        }

        setError(errorMessage);
        throw transformedError; // Re-throw so the UI can handle it
      }
    },
    [registerMutation, router],
  );

  /**
   * Logout user and clear session
   */
  const logout = useCallback(
    async (options?: { redirectTo?: string }) => {
      // Client-local teardown runs exactly once, before the server call —
      // it must happen whether or not the logout endpoint succeeds.
      // `clearClientSessionResidue` also drops the PostHog person identity
      // (FRE-1261): it has to happen here, not in a React effect, because the
      // hard navigation below unloads the page before any user→null effect
      // could run, and a shared device must not keep attributing events /
      // flag buckets to the previous account.
      setError(null);
      setAccessToken(null);
      clearClientSessionResidue();
      setShouldFetchUser(false);

      const loginUrl = crossHostLogin();
      let destination = loginUrl;
      try {
        // Call logout endpoint to clear server-side session.
        // RTK Query will automatically invalidate User and Event tags
        // which clears the cache and sets user to null.
        await logoutMutation().unwrap();
        destination = getValidCrossHostRedirect(options?.redirectTo, loginUrl);
      } catch {
        // Even if the logout request fails, still redirect to login —
        // the client-side session was already torn down above.
      } finally {
        // Plan 4 — login lives on the app host (e.g. app.loci.city), so logout
        // from a city subdomain must hard-navigate cross-host.
        window.location.assign(destination);
      }
    },
    [logoutMutation],
  );

  /**
   * Refresh authentication token
   */
  const refreshToken = useCallback(async () => {
    try {
      setError(null);

      const response = await refreshMutation().unwrap();
      if (response.accessToken) setAccessToken(response.accessToken);

      // Re-fetch user after token refresh
      await refetchUser();
    } catch (err) {
      // Refresh failed - user needs to login again
      // Check if it's a 401 error
      const rtkError = err as Parameters<typeof transformRTKError>[0];

      if (isAuthenticationError(rtkError)) {
        // Plan 4 — refresh-failure must hop back to the app-host login page,
        // not the current host's `/login`, when a city subdomain is active.
        window.location.assign(crossHostLogin());
      } else {
        // Other errors - set error message but don't redirect
        const errorMessage = extractErrorMessage(rtkError, 'Token refresh failed');
        setError(errorMessage);
      }
    }
  }, [refreshMutation, refetchUser]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Set user directly (for compatibility with legacy implementation)
   * This allows manual user state updates while still respecting RTK Query cache
   */
  const setUser = useCallback((newUser: User | null) => {
    setLocalUserOverride(newUser);
  }, []);

  /**
   * Deferred-refetch latch. Set when `bootstrapSession()` is called while the
   * RTK Query subscription is still `uninitialized` — the child-level caller
   * (any protected route shell's `useEffect`) fires BEFORE `useGetCurrentUserQuery`'s
   * internal subscription effect has materialized the cache entry, so we
   * cannot call `refetch()` synchronously. The drain `useEffect` below picks
   * this up once `queryStatus` advances past `uninitialized` and forces a
   * network fetch (so stale-cookie recovery runs even when `/auth/me` would
   * otherwise resolve from cache).
   */
  const pendingBootstrapRefetchRef = useRef(false);

  // Why: `bootstrapSession` is consumed by child effects wired as
  // `useEffect(() => bootstrapSession(), [bootstrapSession])` in protected
  // route shells. If the callback's identity churned on every
  // `queryStatus` transition, every refetch would re-trigger those effects —
  // each refetch moves `queryStatus` `fulfilled -> pending -> fulfilled`,
  // recreating the callback twice per cycle and producing an infinite
  // refetch loop that the backend rate-limiter terminates with 429.
  // Latching both `queryStatus` and `refetchUser` into refs lets the
  // callback read live values at invocation time while keeping its own
  // identity stable across renders.
  const queryStatusRef = useRef(queryStatus);
  useEffect(() => {
    queryStatusRef.current = queryStatus;
  }, [queryStatus]);
  const refetchUserRef = useRef(refetchUser);
  useEffect(() => {
    refetchUserRef.current = refetchUser;
  }, [refetchUser]);

  /**
   * Force-bootstrap the session: refresh the localStorage hint and enable the
   * `/auth/me` query, triggering a network fetch regardless of cache state.
   *
   * Callers:
   * - Protected route shells — after the server layout verifies httpOnly
   *   cookies, any client that reaches a protected route must hydrate `user`
   *   even if the localStorage hint was cleared or never set.
   * - `AppInitializer` (FRE-683) — when the current URL carries the
   *   `OAUTH_RETURN_PARAM` query parameter, the backend has just set auth
   *   cookies via `/api/v1/auth/oauth/<provider>/callback`. The caller is
   *   expected to strip the indicator via `clearOAuthReturnIndicator()`
   *   after calling this so a reload does not re-trigger bootstrap.
   *
   * Interaction with the session hint (`lib/auth/sessionHint.ts`):
   * `bootstrapSession()` *sets* the hint so subsequent navigations skip the
   * gate. The hint is advisory — a true value still requires `/auth/me` to
   * validate. Stale cookies surface here via the recovery effect above and
   * trigger a best-effort logout.
   *
   * Guard rationale: we key on the RTK Query `status` field rather than the
   * local `shouldFetchUser` proxy state. `shouldFetchUser` flips synchronously
   * from `setShouldFetchUser(true)`, but the RTK Query subscription effect
   * that actually initiates the fetch is a parent-level `useEffect` that runs
   * AFTER any child `useEffect` that calls `bootstrapSession()`. Using
   * `shouldFetchUser` as the guard would let a child call `refetch()` on an
   * uninitialized subscription and throw "Cannot refetch a query that has not
   * been started yet". `queryStatus === 'uninitialized'` reflects the actual
   * cache entry, so it only transitions AFTER the subscription effect fires.
   *
   * State transitions: `isLoading` → `true` while `/auth/me` is in flight,
   * then `isLoading` → `false` with `isAuthenticated` resolving to `true`
   * (cookies valid) or `false` (stale/invalid cookies cleared by recovery).
   */
  const bootstrapSession = useCallback(() => {
    setSessionHint();
    if (queryStatusRef.current === 'uninitialized') {
      // Subscription not materialized yet — defer the refetch. Flipping the
      // skip gate kicks off the initial fetch once RTK Query's internal
      // subscription effect runs; the drain effect below then force-refetches
      // so stale-cookie recovery runs even if the initial request would
      // otherwise be served from cache.
      pendingBootstrapRefetchRef.current = true;
      setShouldFetchUser(true);
    } else {
      // Subscription is live (status is pending/fulfilled/rejected). `refetch()`
      // is safe to call synchronously. Clear any pending drain so the refetch
      // fires exactly once for this caller.
      pendingBootstrapRefetchRef.current = false;
      refetchUserRef.current();
    }
  }, []);

  // Drain a deferred bootstrap refetch once the RTK Query subscription has
  // been materialized. This runs after the parent-level subscription effect,
  // so `refetch()` is guaranteed to resolve against a live cache entry.
  useEffect(() => {
    if (queryStatus === 'uninitialized') return;
    if (!pendingBootstrapRefetchRef.current) return;
    pendingBootstrapRefetchRef.current = false;
    refetchUser();
  }, [queryStatus, refetchUser]);

  const value: AuthContextValue = {
    // State
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    loading: isLoading, // Alias for backward compatibility
    error,

    // Actions
    login,
    register,
    logout,
    refreshToken,
    clearError,
    setUser,
    bootstrapSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Hook for auth state only (doesn't include actions)
 */
export function useAuthState() {
  const { user, isAuthenticated, isLoading, error } = useAuth();
  return { user, isAuthenticated, isLoading, error };
}
